import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GetRowIdParams,
  GridReadyEvent,
  MenuItemDef
} from 'ag-grid-enterprise'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import CustomAgGrid from '../../../../../shared/components/custom/Grid/CustomAgGrid'
import { CustomReportTableSection } from '../../../../../redux/context/customReports/typesSection'
import { CustomReportVariable, ExplainerVariable } from '../../../../../redux/context/customReports/typesVariable'
import { useGridRegistration } from './useGridRegistration'

// Erotetut utilit
import { createExcelStyles } from '../../../../../shared/utils/excelStyles'
import { variableRowCellMetadataProvider } from './utils/cellMetadataProviders'
import { createColumnDefinitions, createAutoGroupColumnDef, calculateAutoGroupWidth } from './utils/columnDefinitions'
import { getRowStyle } from './utils/renderingUtils'
import { getStatusBarConfig } from '../../../../../shared/utils/agGridConfig'
import { filtersSelector } from '../../../../../redux/context/filters/selectors'
import CustomReportVoucherModal from '../customReportTable/CustomReportVoucherModal'
import { usePermission } from '../../../../../redux/session/currentUser/hooks'
import { canShowContextMenu } from './utils/contextMenuUtils'
import { CustomColumnContext } from './types'
import { CustomReportCategory } from '../../../../../redux/context/customReports/typesCategory'
import { computeHeaderHeight, computeGroupHeaderHeight } from '../print/CategoryTitleVisitor'
import { getTitleByLanguage } from '../customReportTable/utils'
import { useRowFiltering } from '../../hooks/useRowFiltering'

interface CustomReportProps {
  reportId: number
  section: CustomReportTableSection
}

interface VoucherProps {
  visible: boolean
  row?: ExplainerVariable // Or your row data type
  columnContext?: CustomColumnContext
}

const transformToTreeData = (variables: CustomReportVariable[]): any[] => {
  return variables.map(variable => ({
    ...variable,
    id: variable.id?.toString()
  }))
}

const CustomReportAgGrid: React.FC<CustomReportProps> = ({ reportId, section }) => {
  const { t } = useTranslation()
  const { variables, categories = [] } = section
  const gridRef = useRef<AgGridReact<CustomReportVariable | ExplainerVariable>>(null)
  const { onGridReady: exportOnGridReady } = useGridRegistration(
    section.id,
    getTitleByLanguage(section),
    undefined,
    variableRowCellMetadataProvider
  )
  const { displayEmptyRows } = useSelector(filtersSelector)
  const headerHeight = useMemo(() => computeHeaderHeight(categories), [categories])
  const groupHeaderHeight = useMemo(() => computeGroupHeaderHeight(categories), [categories])
  const context = useRef<{
    reportId: number
    sectionId: number
    categories: CustomReportCategory[]
  }>({
    reportId,
    sectionId: section.id,
    categories
  })

  const [rowData, setRowData] = useState<any[]>([])

  const [voucherProps, setVoucherProps] = useState<VoucherProps>({
    visible: false
  })

  // Use external filter hook for row filtering
  const { isExternalFilterPresent, doesExternalFilterPass } = useRowFiltering(displayEmptyRows)

  const autoGroupWidth = useMemo(() => calculateAutoGroupWidth(variables || []), [variables])

  useEffect(() => {
    const treeData = transformToTreeData(variables || [])
    setRowData(treeData)
    // Force AG-Grid to re-evaluate cellStyle (e.g. row colors) after data update
    const timeoutId = setTimeout(() => {
      gridRef.current?.api?.redrawRows()
    }, 0)
    return () => clearTimeout(timeoutId)
  }, [variables])

  const popupParent = useMemo<HTMLElement | null>(() => {
    return document.querySelector('body')
  }, [])

  const columnDefs = useMemo(() => createColumnDefinitions(categories), [categories])
  const autoGroupColumnDef = useMemo(() => ({ ...createAutoGroupColumnDef(), width: autoGroupWidth }), [autoGroupWidth])
  const excelStyles = createExcelStyles()
  const canReadVouchers = usePermission('read-vouchers')
  const getRowId = useCallback((params: GetRowIdParams) => String(params.data.id), [])

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      exportOnGridReady(params)
    },
    [exportOnGridReady]
  )

  const onVoucherModalCancel = () => {
    setVoucherProps({
      visible: false
    })
  }

  const getContextMenuItems = useCallback(
    (
      params: GetContextMenuItemsParams<CustomReportVariable | ExplainerVariable>
    ): (DefaultMenuItem | MenuItemDef)[] | Promise<(DefaultMenuItem | MenuItemDef)[]> => {
      const { context: columnContext } = params.column?.getColDef() || {}
      const { column, node } = params

      // Tarkista onko context menu sallittu ennen menu itemien luomista
      if (!node?.data || !columnContext) {
        return [] // Ei dataa tai kategoriaa, ei menu itemeja
      }

      // Käytä canShowContextMenu funktiota
      const shouldShowVoucherMenu = canShowContextMenu(node.data, columnContext, canReadVouchers)
      // Jos ei saa näyttää voucher menua, palauta tyhjä array
      if (!shouldShowVoucherMenu) {
        return []
      }

      // Luo menu itemit vain jos voucher menu on sallittu
      const result: (DefaultMenuItem | MenuItemDef)[] = [
        {
          name: t('global:show-vouchers'),
          action: () => {
            if (column && node?.data) {
              setVoucherProps({
                row: node.data as unknown as ExplainerVariable,
                visible: true,
                columnContext
              })
            }
          },
          icon: '🔍︎'
        }
      ]

      return result
    },
    [canReadVouchers, t] // Lisää tarvittavat dependencyt
  )

  return (
    <>
      <CustomReportVoucherModal
        onCancel={onVoucherModalCancel}
        visible={voucherProps.visible}
        columnContext={voucherProps.columnContext}
        row={voucherProps.row}
      />
      <div data-cy={`section-grid-${section.id}`} style={{ height: '100%' }}>
        <CustomAgGrid
          className="custom-report-grid"
          treeData
          enableRowPinning
          excludeChildrenWhenTreeDataFiltering
          treeDataParentIdField="parentId"
          rowData={rowData}
          ref={gridRef}
          context={context.current}
          groupHeaderHeight={groupHeaderHeight}
          headerHeight={headerHeight}
          suppressGroupRowsSticky
          columnDefs={columnDefs}
          excelStyles={excelStyles}
          popupParent={popupParent}
          autoGroupColumnDef={autoGroupColumnDef}
          getRowId={getRowId}
          onGridReady={onGridReady}
          getRowStyle={getRowStyle}
          getContextMenuItems={getContextMenuItems}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          cellSelection
          skipHeaderOnAutoSize
          statusBar={getStatusBarConfig()}
        />
      </div>
    </>
  )
}

export default CustomReportAgGrid
